import {ThunkDispatch} from "@reduxjs/toolkit";
import React, { useRef, useState, useEffect, useCallback } from 'react';
import {connect, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"

import {selectItemImageData, selectProcessedImageData, selectItemRemovalAfterImageView, selectCanAddFileItemRemoval, selectServiceSettings} from '../../selectors'
import {itemRemovalActions, ItemImage, AfterImage} from '../../reducers/itemRemoval'
import { serviceSettingsActions, WATERMARK_TYPE } from '../../reducers/serviceSettings'
import {toastActions} from '../../reducers/toast'
import MyPageModal from '../mypage/MyPageModal'
import { Tooltip } from 'primereact/tooltip'
import { InfoIcon } from '../SVGIcons'
import { isWatermarkEnabled, isAnnotationEnabled, fetchImageAsBlob } from '../../utils/watermark'
import { reapplyWatermarksOnSettingsChange } from '../../utils/watermarkHelpers'
import { useServiceSettings } from '../../hooks/useServiceSettings'
import { useWatermarkSettings } from '../../hooks/useWatermarkSettings'

import ExamplePhotoNG from '../ExamplePhotoNG'
import {ReloadRoomDesignIcon, DelPhotoIcon, AddMorePhotoIcon, LoadingIcon,
  AfterImageDownloadIcon} from '../SVGIcons'

import ItemRemovalEditActionButtons from './ItemRemovalEditActionButtons'
import FurnitureRemovalModeToggle from './FurnitureRemovalModeToggle'
import FurnitureRemovalDrawingPopup from './FurnitureRemovalDrawingPopup'

// State型定義
interface RootState {
  itemRemoval: {
    itemImages: ItemImage[];
    maxExecTimePerImage: number;
    runTime: number;
    isLoading: boolean;
    selectedItemImage?: number;
    furnitureMaskPreviewUrl: string | null;
    furnitureRemovalMode: 'full' | 'partial';
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  const selectedItemImageData = selectItemImageData(state)
  const {selectedProcessedImages, selectedProcessedImageData, selectedProcessedImageIndex} = selectProcessedImageData(state)
  const afterImageView = selectItemRemovalAfterImageView(state)
  const canAddFile = selectCanAddFileItemRemoval(state)
  const watermarkSettings = selectServiceSettings(state)

  // ウォーターマークまたは注釈が有効かチェック
  const hasWatermarkSettings = watermarkSettings && (isWatermarkEnabled(watermarkSettings) || isAnnotationEnabled(watermarkSettings))

  return { ...state.itemRemoval,
    selectedItemImageData,
    selectedAfterImages: selectedProcessedImages,
    selectedAfterImageData: selectedProcessedImageData,
    selectedAfterImageIndex: selectedProcessedImageIndex,
    afterImageView,
    canAddFile,
    watermarkSettings,
    hasWatermarkSettings,
    serviceSettings: state.serviceSettings, // serviceSettings全体も渡す
    furnitureMaskPreviewUrl: state.itemRemoval.furnitureMaskPreviewUrl,
    furnitureRemovalMode: state.itemRemoval.furnitureRemovalMode,
    isFurnitureRemovalProcessing: state.itemRemoval.isFurnitureRemovalProcessing,
  }
}

// ドラッグ＆ドロップイベントの型定義
interface DragEvent extends React.DragEvent<HTMLLabelElement> {
  dataTransfer: DataTransfer;
}

// コンポーネントのProps型定義
interface ItemRemovalEditFormSomeProps {
  itemImages: ItemImage[];
  maxExecTimePerImage: number;
  runTime: number;
  isLoading: boolean;
  selectedItemImageData?: ItemImage;
  selectedAfterImages: AfterImage[];
  selectedAfterImageData?: AfterImage;
  selectedAfterImageIndex: number;
  stubId: string;
  afterImageView: string;
  canAddFile: boolean;
  watermarkGlobalEnabled: boolean;
  processingWatermarks: string[];
  watermarkSettings: any;
  hasWatermarkSettings: boolean;
  serviceSettings: any; // serviceSettings全体
  furnitureMaskPreviewUrl: string | null;
  furnitureRemovalMode: 'full' | 'partial';
  isFurnitureRemovalProcessing: boolean;
}

const watermarkTooltip = '・「アカウント設定」で設定したロゴを画像上に表示する機能。\n・設定を変更する場合は、「設定はこちら」をクリック。';

const ItemRemovalEditFormSome = (props: ItemRemovalEditFormSomeProps) => {
  const {
    itemImages,
    maxExecTimePerImage,
    runTime,
    isLoading,
    selectedItemImageData,
    selectedAfterImages,
    selectedAfterImageData,
    selectedAfterImageIndex,
    afterImageView,
    canAddFile,
    watermarkGlobalEnabled,
    processingWatermarks,
    watermarkSettings,
    hasWatermarkSettings,
    furnitureMaskPreviewUrl,
    furnitureRemovalMode,
    isFurnitureRemovalProcessing
  } = props
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
  const navigate = useNavigate()
  const inputFile = useRef<HTMLInputElement | null>(null);
  const itemImageRef = useRef<HTMLImageElement | null>(null);
  const afterImageRef = useRef<HTMLImageElement | null>(null);
  const [afterImageLoaded, setAfterImageLoaded] = useState(false);

  // 画像のURLを計算（watermark有無を含む）
  const displayImageUrl = watermarkGlobalEnabled && selectedAfterImageData?.watermarkedURI
    ? selectedAfterImageData.watermarkedURI
    : selectedAfterImageData?.serverURI;

  // マイページモーダルの表示状態
  const [showMyPageModal, setShowMyPageModal] = useState(false)

  // カスタムフックを使用
  const { lastProcessedTimestamp, setLastProcessedTimestamp } = useServiceSettings();


  // serviceSettingsのlastSavedTimestampを監視して、設定保存を検知
  const savedTimestamp = props.serviceSettings?.lastSavedTimestamp;

  // カスタムフックでウォーターマーク設定を管理
  const { handleWatermarkToggle } = useWatermarkSettings({
    savedTimestamp,
    lastProcessedTimestamp,
    setLastProcessedTimestamp,
    watermarkGlobalEnabled,
    selectedAfterImages,
    watermarkSettings,
    hasWatermarkSettings,
    clearAllWatermarksAction: itemRemovalActions.clearAllWatermarks,
    applyWatermarkToImageAction: itemRemovalActions.applyWatermarkToImage,
    setWatermarkGlobalEnabledAction: itemRemovalActions.setWatermarkGlobalEnabled,
    toggleAllWatermarksAction: itemRemovalActions.toggleAllWatermarks,
  });

  useEffect(() => {
    setAfterImageLoaded(false);

    // 選択された画像をプリロード
    if (selectedAfterImageData && displayImageUrl) {
      const img = new Image();
      img.src = displayImageUrl;
      img.onload = () => {
        setAfterImageLoaded(true);
      };
      img.onerror = (e) => {
        setAfterImageLoaded(false);
      };
    }
  }, [selectedItemImageData, selectedAfterImageData, selectedAfterImageIndex, displayImageUrl]);


  // ウォーターマークトグルのハンドラー（モーダル表示を含む）
  const handleWatermarkToggleWithModal = useCallback((enabled: boolean) => {
    handleWatermarkToggle(enabled, () => setShowMyPageModal(true));
  }, [handleWatermarkToggle]);

  // 画像ダウンロードのハンドラー（ウォーターマーク対応）
  const handleDownloadImage = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!selectedAfterImageData) return

    try {
      const imageUrl = watermarkGlobalEnabled && selectedAfterImageData.watermarkedURI
        ? selectedAfterImageData.watermarkedURI
        : selectedAfterImageData.serverURI

      // クロスオリジンURLでもダウンロードできるようにBlobに変換
      const blob = await fetchImageAsBlob(imageUrl)
      const blobUrl = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = blobUrl
      a.download = selectedAfterImageData.fileName || 'image.jpg'
      a.click()

      // メモリリーク防止
      URL.revokeObjectURL(blobUrl)

      dispatch(toastActions.toastSuccess('画像のダウンロードを開始しました'))
    } catch (error) {
      console.error('Download failed:', error)
      dispatch(toastActions.toastError('画像のダウンロードに失敗しました'))
    }
  }, [selectedAfterImageData, watermarkGlobalEnabled, dispatch])

  const maxExecTime = maxExecTimePerImage
  const processPercent = runTime < maxExecTime*0.9 ? runTime / maxExecTime : 0.9
  const displayPercent = Math.floor(processPercent * 100) + "%"
  const strokeDashoffset = 100 - 100 * processPercent

  const onButtonAddImage = () => {
    if (!inputFile || !inputFile.current || isLoading) return
    inputFile.current.click();
  }

  const addItemImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading || !event.target.files) return
    const formData = {
      stubId: props.stubId.toString(),
      files: event.target.files
    };
    dispatch(itemRemovalActions.addItemImages(formData))
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    if (isLoading) return

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFiles = e.dataTransfer.files
      const formData = {
        stubId: props.stubId.toString(),
        files: droppedFiles
      };
      dispatch(itemRemovalActions.addItemImages(formData))
    }
  };

  const deleteSelectedItem = () => {
    if (isLoading) return
    dispatch(itemRemovalActions.deleteSelectedItem())
  }

  // 特定のstubIdを持つ画像を削除する関数
  const deleteItemByStubId = (stubId: number) => {
    if (isLoading) return

    // 指定されたstubIdの画像を除外した新しい配列を作成
    const newItemImages = itemImages.filter(img => img.stubId !== stubId);

    // 選択中の画像が削除対象だった場合、選択状態をクリア
    if (selectedItemImageData && selectedItemImageData.stubId === stubId) {
      dispatch(itemRemovalActions.setSelectedItemImage(undefined));
    }

    // 画像リストを更新
    dispatch(itemRemovalActions.setItemImages(newItemImages));
  }

  const setSelectedItemImage = (stubId: number) => {
    dispatch(itemRemovalActions.setSelectedItemImage(stubId))
  }

  const setSelectedAfterImage = useCallback(async (id: number) => {
    dispatch(itemRemovalActions.setSelectedAfterImage(id))

    // ウォーターマークが有効で、選択した画像にまだ適用されていない場合
    if (watermarkGlobalEnabled) {
      const image = selectedAfterImages.find((img: AfterImage) => img.id === id)
      if (image && !image.watermarkedURI && !image.isProcessingWatermark) {
        await dispatch(itemRemovalActions.applyWatermarkToImage({
          imageId: image.id,
          imageUrl: image.serverURI
        }))
      }
    }
  }, [dispatch, watermarkGlobalEnabled, selectedAfterImages])

  const handleAfterImageLoaded = () => {
    setAfterImageLoaded(true);
  }

  const handleAfterImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setAfterImageLoaded(false);
  }

  const reloadPage = () => {
    dispatch(itemRemovalActions.clearData())
  }

  return (
    <>
    <style>{`
      .orange-tooltip .p-tooltip-text {
        background-color: rgb(249 115 22);
        color: white;
        white-space: pre-line;
      }
      .orange-tooltip .p-tooltip-arrow {
        border-right-color: rgb(249 115 22);
      }
    `}</style>
    <Tooltip target=".tooltip-watermark-item" className="orange-tooltip max-w-xs" />
    <div className="max-w-8xl w-full mx-auto pt-1 pb-3 px-4 sm:px-6 md:px-8 text-[#2d2d2d]">
      <div className="grid grid-cols-9 gap-2">
        <div className="col-1 bg-gray-200 overflow-y-auto rounded min-h-[30.28rem] flex flex-col p-2">
          <button type="button" onClick={reloadPage}
            disabled={isLoading || (selectedItemImageData && selectedItemImageData.isProcessed)}
            className={!isLoading && !(selectedItemImageData && selectedItemImageData.isProcessed)
              ? "bg-gray-50 flex p-2 rounded-md border border-yellow-50 mb-4 text-sm text-yellow-700 items-center justify-center"
              : "bg-gray-300 flex p-2 rounded-md mb-4 text-sm items-center justify-center"
            }>
            <ReloadRoomDesignIcon />一括削除
          </button>
          <ul className="space-y-2">
            {itemImages.map((itemImage: any) => (
              <li key={itemImage.stubId} onClick={e => setSelectedItemImage(itemImage.stubId)}
                className={itemImage.stubId === selectedItemImageData?.stubId
                  ? "flex items-center justify-center w-full bg-gray-300 h-24 w-28 overflow-hidden mx-auto rounded-sm cursor-pointer relative ring-offset-2 ring-2 ring-blue-500"
                  : "flex items-center justify-center w-full bg-gray-300 h-24 w-28 overflow-hidden mx-auto rounded-sm cursor-pointer relative"}>
                {itemImage.isProcessed && (
                  <span className="bg-[#ff4600cc] absolute top-1 left-1 text-xs text-white rounded-lg px-1">処理済</span>
                )}
                {/* 処理済みでない場合のみ削除ボタンを表示 */}
                {!isLoading && !itemImage.isProcessed && (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      deleteItemByStubId(itemImage.stubId);
                    }}
                    className="absolute top-1 right-1 opacity-50 hover:opacity-70"
                    title="画像を削除"
                  >
                    <DelPhotoIcon className="w-4 h-4" />
                  </button>
                )}
                <img src={itemImage.uri} className="block w-full" alt=""/>
              </li>
            ))}

            {/* 画像追加ボタンの表示条件をcanAddFileだけにする */}
            {canAddFile && (
              <li className="text-center">
                <label>
                  <button
                    type="button"
                    onClick={onButtonAddImage}
                    className="text-blue-600 hover:text-blue-600 rounded-full hover:bg-gray-100"
                    title="画像を追加"
                  >
                    <AddMorePhotoIcon className="w-6 h-6" />
                  </button>
                  <input
                    type="file"
                    ref={inputFile}
                    multiple
                    accept="image/png, image/jpeg, image/webp"
                    onChange={addItemImages}
                    className="hidden"
                    aria-label="画像ファイルを選択"
                  />
                </label>
              </li>
            )}
          </ul>
        </div>

        <div
          className="grid grid-cols-2 col-span-8 border border-gray-300 bg-white overflow-hidden rounded"
          style={{ height: '528px', maxHeight: '528px' }}>
          <div className="w-full col-1 bg-white flex flex-col p-3" style={{height: '100%', maxHeight: '528px', overflow: 'hidden'}}>
              {/* モード切り替えトグル（一部モードでプレビューがない場合に表示） */}
              {selectedItemImageData && !selectedItemImageData.processedWithPartialMode && !furnitureMaskPreviewUrl && (
                <div className="flex justify-center mb-2" style={{flexShrink: 0}}>
                  <FurnitureRemovalModeToggle
                    selectedImageUrl={selectedItemImageData.uri}
                    disabled={isLoading}
                    partialDisabled={selectedItemImageData.isProcessed || (selectedItemImageData?.isProcessedFail && furnitureRemovalMode === 'full')}
                  />
                </div>
              )}

              {/* 一部モード時のヘッダー（常に表示、処理中はボタンをdisable） */}
              {furnitureRemovalMode === 'partial' && (
                <div className="flex justify-between items-center mb-2" style={{flexShrink: 0}}>
                  <span className="text-sm text-gray-600">
                    {furnitureMaskPreviewUrl ? '選択範囲プレビュー' : '一部家具消しモード'}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        // プレビューをクリアして描画画面を開く
                        dispatch(itemRemovalActions.clearFurnitureMaskPreview())
                        // 再編集フラグをセット（マスク線を維持）
                        dispatch(itemRemovalActions.setFurnitureMaskIsReEdit(true))
                        if (selectedItemImageData) {
                          dispatch(itemRemovalActions.setFurnitureRemovalOriginalImageUrl(selectedItemImageData.uri))
                        }
                        dispatch(itemRemovalActions.setShowFurnitureRemovalDrawing(true))
                      }}
                      disabled={isLoading || isFurnitureRemovalProcessing}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        isLoading || isFurnitureRemovalProcessing
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      編集
                    </button>
                    <button
                      onClick={() => {
                        // プレビューをクリアして全体モードに戻す
                        dispatch(itemRemovalActions.clearFurnitureMaskPreview())
                        dispatch(itemRemovalActions.clearFurnitureMaskLines())
                        dispatch(itemRemovalActions.setFurnitureRemovalMode('full'))
                        // 画像のマスク情報もクリア
                        if (selectedItemImageData) {
                          dispatch(itemRemovalActions.clearImageMaskData(selectedItemImageData.stubId))
                        }
                      }}
                      disabled={isLoading || isFurnitureRemovalProcessing || (selectedItemImageData?.processedWithPartialMode ?? false)}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        isLoading || isFurnitureRemovalProcessing || selectedItemImageData?.processedWithPartialMode
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      取消
                    </button>
                  </div>
                </div>
              )}

              <div style={{flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                {/* プレビューがある場合はプレビューを表示 */}
                {furnitureMaskPreviewUrl && furnitureRemovalMode === 'partial' ? (
                  <img
                    src={furnitureMaskPreviewUrl}
                    style={{maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block', borderRadius: '0.25rem'}}
                    alt="マスクプレビュー"
                  />
                ) : (
                  <>
                    {selectedItemImageData && canAddFile && (
                      <label htmlFor="file-input" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%'}}>
                        <img ref={itemImageRef} src={selectedItemImageData.uri} style={{maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block', borderRadius: '0.25rem'}} alt=""/>
                        <input
                          id="file-input"
                          type="file"
                          multiple
                          accept="image/png, image/jpeg, image/webp"
                          onChange={addItemImages}
                          style={{display: 'none'}}
                          aria-label="画像ファイルを選択"
                        />
                      </label>
                    )}
                    {selectedItemImageData && !canAddFile && (
                        <img ref={itemImageRef} src={selectedItemImageData.uri} style={{maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block', borderRadius: '0.25rem'}} alt=""/>
                    )}
                  </>
                )}
              </div>
          </div>

          {/* 条件分岐の順序を変更: show_gen_image_errorを最優先 */}
          {afterImageView === 'show_gen_image_error' && (
            <div className="p-3 col-1 border-s border-gray-300 flex">
              <div className="relative m-auto text-red-600 text-center text-lg">
                画像処理に失敗しました。<br />
                再度処理を行ってください。<br />
                ※クレジットは消費されておりません。
              </div>
            </div>
          )}
          {afterImageView === 'show_loading' && (
            <div className="p-3 col-1 border-s border-gray-300 flex">
              <div className="relative size-40 m-auto">
                <svg className="size-full -rotate-90" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="18" cy="18" r="16" fill="none" className="stroke-current text-gray-200"
                    strokeWidth="2" />
                  <circle cx="18" cy="18" r="16" fill="none" className="stroke-current text-blue-600"
                    strokeWidth="2" strokeDasharray="100" strokeDashoffset={strokeDashoffset} strokeLinecap="round"/>
                </svg>
                <div className="absolute top-1/2 start-1/2 transform -translate-y-1/2 -translate-x-1/2">
                  <span className="text-center text-2xl font-bold text-blue-600">{displayPercent}</span>
                </div>
              </div>
            </div>
          )}
          {afterImageView === 'show_blank' && (
            <ExamplePhotoNG tabName="itemRemoval" />
          )}
          {afterImageView === 'show_images' && (
            <div className="relative w-full col-1 bg-white border-s border-gray-300 h-full flex flex-col p-3">
              {selectedAfterImages.length > 0 && (
                <div className="flex justify-between items-center mb-2">
                  <div className="grid grid-cols-9 gap-1">
                    {selectedAfterImages.map((image: AfterImage, index: number) => (
                      <button key={image.id} onClick={() => setSelectedAfterImage(image.id)} className={selectedAfterImageData && selectedAfterImageData.id === image.id
                        ? "min-w-6 min-h-6 submit-btn text-white hover:text-gray-400 rounded"
                        : "min-w-6 min-h-6 bg-gray-800 text-white hover:text-gray-400 rounded"
                      }>{index+1}</button>
                    ))}
                  </div>
                  {/* ウォーターマークトグル */}
                  <div className="flex items-center">
                    <label className="flex items-center bg-white/90 rounded-lg px-3 py-1 shadow-sm cursor-pointer hover:bg-white/95 transition-colors">
                      <input
                        type="checkbox"
                        checked={watermarkGlobalEnabled}
                        onChange={(e) => handleWatermarkToggleWithModal(e.target.checked)}
                        disabled={!hasWatermarkSettings || processingWatermarks.length > 0}
                        className="mr-2 w-4 h-4"
                        style={{ accentColor: '#ff4500' }}
                      />
                      <span className="text-sm font-medium">ウォーターマーク</span>
                    </label>
                    <InfoIcon
                      className="tooltip-watermark-item ml-1 mr-2 w-4 h-4 cursor-pointer text-gray-400"
                      data-pr-tooltip={watermarkTooltip}
                      data-pr-position="left"
                    />

                    <span className="ml-2 text-xs">
                      設定は<button
                        onClick={() => setShowMyPageModal(true)}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >こちら</button>
                    </span>
                  </div>
                </div>
              )}
              <div className="relative flex-1 flex items-center justify-center">
                {selectedAfterImageData && (
                    <div className="relative group h-full w-full flex items-center justify-center">
                      <div className="duration-100">
                        <div className='absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 z-30 flex items-center'>
                          <button
                            onClick={handleDownloadImage}
                            className="w-[72px] h-[72px] rounded-full bg-white/95 hover:bg-white/90 hidden group-hover:flex-col justify-center items-center z-30"
                            title="画像をダウンロード"
                          >
                            <AfterImageDownloadIcon className="w-6 h-6 mb-1" />
                            <p className='text-sm text-center'>保存</p>
                          </button>
                        </div>
                        <span className="absolute group-hover:inset-0 group-hover:bg-gray-500/60 z-10" />
                        {!afterImageLoaded && (
                          <div className="flex justify-center items-center h-32">
                            <span className="text-gray-500">画像を読み込み中...</span>
                          </div>
                        )}
                        <img
                          ref={afterImageRef}
                          src={displayImageUrl}
                          style={{display: afterImageLoaded ? 'block': 'none', maxHeight: 'calc(528px - 24px)', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain'}}
                          className="block"
                          alt=""
                          onLoad={handleAfterImageLoaded}
                          onError={handleAfterImageError}
                          crossOrigin="anonymous"
                          key={`after-image-${selectedAfterImageData.id}`}
                        />
                      </div>
                    </div>
                )}
                {!selectedAfterImageData && (
                  <div className="p-3 col-1 border-s border-gray-300 flex">
                    <div className="relative m-auto text-gray-600 text-center text-lg">
                      処理済み画像がありません
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <ItemRemovalEditActionButtons />
    </div>

    {/* マイページモーダル */}
    {showMyPageModal && (
      <MyPageModal
        initialTab="service"
        initialExpandWatermark={true}
        onClose={() => {
          setShowMyPageModal(false)
          // モーダルを閉じた後、ServiceSettingsを再取得
          dispatch(serviceSettingsActions.fetchServiceSettings())
        }}
      />
    )}

    {/* 家具部分消去描画ポップアップ */}
    <FurnitureRemovalDrawingPopup />
  </>
  )
}

export default connect(mapStateToProps)(ItemRemovalEditFormSome)